import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import {
  ArrowLeft, Plus, Search, Filter, Edit3, Trash2, 
  Building2, MapPin, Calendar, Users, Settings,
  Factory, Phone, Mail, Clock, AlertTriangle,
  CheckCircle2, User, X, Save, Sparkles,
  Globe, Shield, Award, Star, Zap
} from 'lucide-react';
import { 
  getPlants, 
  createPlant, 
  updatePlant, 
  deletePlant,
  getAvailableAdmins,
  assignAdminToPlant 
} from '../../services/operations/superAdminAPI';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../utils/utils';
import toast from 'react-hot-toast';

const PlantManagement = ({ onBack, onRefresh }) => {
  const { token } = useSelector(state => state.auth);
  
  const [plants, setPlants] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);

  const [plantForm, setPlantForm] = useState({
    name: '',
    location: '',
    code: '',
    description: '',
    adminEmail: '',
    establishedDate: '',
    contactInfo: {
      phone: '',
      email: ''
    },
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    operationalHours: {
      start: '',
      end: ''
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [plantsResponse, adminsResponse] = await Promise.all([
        getPlants(token),
        getAvailableAdmins(token)
      ]);
      
      if (plantsResponse?.success) {
        setPlants(plantsResponse.plants);
      }
      if (adminsResponse?.success) {
        setAvailableAdmins(adminsResponse.admins);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setPlantForm({
      name: '',
      location: '',
      code: '',
      description: '',
      adminEmail: '',
      establishedDate: '',
      contactInfo: { phone: '', email: '' },
      address: { street: '', city: '', state: '', country: '', zipCode: '' },
      operationalHours: { start: '', end: '' }
    });
  };

  const handleCreatePlant = async (e) => {
    e.preventDefault();
    try {
      const response = await createPlant(plantForm, token);
      if (response?.success) {
        await fetchData();
        onRefresh?.();
        setShowCreateModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating plant:', error);
    }
  };

  const handleUpdatePlant = async (e) => {
    e.preventDefault();
    if (!selectedPlant) return;
    
    try {
      const response = await updatePlant(selectedPlant._id, plantForm, token);
      if (response?.success) {
        await fetchData();
        onRefresh?.();
        setShowEditModal(false);
        setSelectedPlant(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error updating plant:', error);
    }
  };

  const handleDeletePlant = async (plantId) => {
    if (!window.confirm('Are you sure you want to delete this plant? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await deletePlant(plantId, token);
      if (response?.success) {
        await fetchData();
        onRefresh?.();
      }
    } catch (error) {
      console.error('Error deleting plant:', error);
    }
  };

  const handleAssignAdmin = async (adminId) => {
    if (!selectedPlant) return;
    
    try {
      const response = await assignAdminToPlant(selectedPlant._id, adminId, token);
      if (response?.success) {
        await fetchData();
        onRefresh?.();
        setShowAssignModal(false);
        setSelectedPlant(null);
      }
    } catch (error) {
      console.error('Error assigning admin:', error);
    }
  };

  const openEditModal = (plant) => {
    setSelectedPlant(plant);
    setPlantForm({
      name: plant.name || '',
      location: plant.location || '',
      code: plant.code || '',
      description: plant.description || '',
      adminEmail: plant.adminEmail || '',
      establishedDate: plant.establishedDate ? plant.establishedDate.split('T')[0] : '',
      contactInfo: {
        phone: plant.contactInfo?.phone || '',
        email: plant.contactInfo?.email || ''
      },
      address: {
        street: plant.address?.street || '',
        city: plant.address?.city || '',
        state: plant.address?.state || '',
        country: plant.address?.country || '',
        zipCode: plant.address?.zipCode || ''
      },
      operationalHours: {
        start: plant.operationalHours?.start || '',
        end: plant.operationalHours?.end || ''
      }
    });
    setShowEditModal(true);
  };

  const openAssignModal = (plant) => {
    setSelectedPlant(plant);
    setShowAssignModal(true);
  };

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || plant.status.toLowerCase() === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-16 w-16 text-blue-600 mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen  relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-indigo-500/5 to-cyan-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Header */}
      <motion.div 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 100 }}
        className="relative z-10"
      >
        <div className="bg-white/5 backdrop-blur-2xl ">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl shadow-2xl">
                    <Factory className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-black to-black bg-clip-text text-transparent">
                      Plant Management
                    </h1>
                    <p className="text-black/70 text-lg mt-1">Create, manage, and assign administrators to plants</p>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={() => setShowCreateModal(true)}
                className="gap-2 shadow-lg hover:shadow-xl text-white"
              >
                <Plus className="h-5 w-5" />
                Add Plant
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Card className="bg-gradient-to-r from-white/10 to-white/5 border-black/20">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search plants by name, code, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-black/60 transition-all duration-300"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-black/60 h-5 w-5" />
                </div>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-6 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black transition-all duration-300"
                >
                  <option value="all" className="text-slate-800 bg-white">All Status</option>
                  <option value="active" className="text-slate-800 bg-white">Active</option>
                  <option value="inactive" className="text-slate-800 bg-white">Inactive</option>
                  <option value="maintenance" className="text-slate-800 bg-white">Maintenance</option>
                </select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Plants Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {filteredPlants.map((plant) => (
              <motion.div 
                key={plant._id} 
                variants={itemVariants}
                layout
                whileHover={{ scale: 1.02, y: -5 }}
                className="group"
              >
                <Card className="h-full bg-gradient-to-br from-white/10 to-white/5 border-black/20 hover:border-black/40 transition-all duration-300 overflow-hidden">
                  {/* Plant Card Header */}
                  <div className="p-6 border-b border-black/10">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <motion.div 
                          whileHover={{ rotate: 10, scale: 1.1 }}
                          className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl shadow-lg"
                        >
                          <Factory className="h-6 w-6 text-black" />
                        </motion.div>
                        <div>
                          <h3 className="font-bold text-black text-lg">{plant.name}</h3>
                          <p className="text-black/60 font-medium">{plant.code}</p>
                        </div>
                      </div>
                      
                      <Badge 
                        variant={
                          plant.status === 'Active' ? 'success' :
                          plant.status === 'Inactive' ? 'destructive' : 'warning'
                        }
                        className="font-medium"
                      >
                        {plant.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Plant Details */}
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-3 text-black/80">
                      <MapPin className="h-4 w-4 text-blue-400" />
                      <span className="text-sm font-medium">{plant.location}</span>
                    </div>
                    
                    {plant.adminId ? (
                      <div className="flex items-center gap-3 text-black/80">
                        <User className="h-4 w-4 text-green-400" />
                        <span className="text-sm font-medium">{plant.adminId.firstName} {plant.adminId.lastName}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 text-amber-400">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-sm font-medium">No admin assigned</span>
                      </div>
                    )}
                    
                    {plant.establishedDate && (
                      <div className="flex items-center gap-3 text-black/80">
                        <Calendar className="h-4 w-4 text-purple-400" />
                        <span className="text-sm font-medium">Est. {new Date(plant.establishedDate).getFullYear()}</span>
                      </div>
                    )}
                    
                    {plant.description && (
                      <p className="text-sm text-black/70 line-clamp-2 mt-3">{plant.description}</p>
                    )}
                  </CardContent>

                  {/* Actions */}
                  <div className="p-6 border-t border-white/10 flex gap-2">
                    <Button
                      onClick={() => openEditModal(plant)}
                      variant="secondary"
                      size="sm"
                      className="flex-1 gap-2"
                    >
                      <Edit3 className="h-4 w-4" />
                      Edit
                    </Button>
                    
                    <Button
                      onClick={() => openAssignModal(plant)}
                      variant="success"
                      size="sm"
                      className="flex-1 gap-2"
                    >
                      <Users className="h-4 w-4" />
                      Assign
                    </Button>
                    
                    <Button
                      onClick={() => handleDeletePlant(plant._id)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredPlants.length === 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-3xl w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <Factory className="h-12 w-12 text-black/60" />
            </div>
            <h3 className="text-2xl font-bold text-black mb-2">No plants found</h3>
            <p className="text-black/60 text-lg">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first plant to get started'
              }
            </p>
          </motion.div>
        )}
      </div>

      {/* Create Plant Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className=" bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/20 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                      <Plus className="h-6 w-6 text-black" />
                    </div>
                    <h2 className="text-2xl font-bold text-black">Create New Plant</h2>
                  </div>
                  <button
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    className="p-2 text-black/60 hover:text-black hover:bg-white/10 rounded-xl transition-all"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreatePlant} className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Plant Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={plantForm.name}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="Enter plant name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Plant Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={plantForm.code}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="e.g., PLT001"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      required
                      value={plantForm.location}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="City, State"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Admin Email
                    </label>
                    <input
                      type="text"
                      value={plantForm.adminEmail}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, adminEmail: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="e.g., 1000 units/day"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black/80 mb-2">
                    Description
                  </label>
                  <textarea
                    value={plantForm.description}
                    onChange={(e) => setPlantForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all resize-none"
                    placeholder="Brief description of the plant"
                  />
                </div>

                {/* Contact Information */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-blue-400" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-black/80 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={plantForm.contactInfo.phone}
                        onChange={(e) => setPlantForm(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, phone: e.target.value }
                        }))}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                        placeholder="Phone number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-black/80 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={plantForm.contactInfo.email}
                        onChange={(e) => setPlantForm(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, email: e.target.value }
                        }))}
                        className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-black/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                        placeholder="Email address"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-black/10">
                  <Button
                    type="button"
                    onClick={() => { setShowCreateModal(false); resetForm(); }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Create Plant
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Plant Modal */}
      <AnimatePresence>
        {showEditModal && selectedPlant && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/20 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
                      <Edit3 className="h-6 w-6 text-black" />
                    </div>
                    <h2 className="text-2xl font-bold text-black">Edit Plant</h2>
                  </div>
                  <button
                    onClick={() => { setShowEditModal(false); setSelectedPlant(null); resetForm(); }}
                    className="p-2 text-black/60 hover:text-black hover:bg-white/10 rounded-xl transition-all"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleUpdatePlant} className="p-6 space-y-6">
                {/* Same form fields as create modal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Plant Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={plantForm.name}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="Enter plant name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black/80 mb-2">
                      Plant Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={plantForm.code}
                      onChange={(e) => setPlantForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-black placeholder-white/60 transition-all"
                      placeholder="e.g., PLT001"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-white/10">
                  <Button
                    type="button"
                    onClick={() => { setShowEditModal(false); setSelectedPlant(null); resetForm(); }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Update Plant
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Assign Admin Modal */}
      <AnimatePresence>
        {showAssignModal && selectedPlant && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl max-w-md w-full border border-white/20 shadow-2xl"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl">
                      <Users className="h-6 w-6 text-black" />
                    </div>
                    <h2 className="text-2xl font-bold text-black">Assign Administrator</h2>
                  </div>
                  <button
                    onClick={() => { setShowAssignModal(false); setSelectedPlant(null); }}
                    className="p-2 text-black/60 hover:text-black hover:bg-white/10 rounded-xl transition-all"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <p className="text-black/70 mt-2">
                  Assign an administrator to <strong className="text-black">{selectedPlant.name}</strong>
                </p>
              </div>

              <div className="p-6">
                {availableAdmins.length > 0 ? (
                  <div className="space-y-3">
                    {availableAdmins.map((admin) => (
                      <motion.div
                        key={admin._id}
                        whileHover={{ scale: 1.02 }}
                        className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all duration-300"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl">
                            <User className="h-5 w-5 text-black" />
                          </div>
                          <div>
                            <p className="font-semibold text-black">
                              {admin.firstName} {admin.lastName}
                            </p>
                            <p className="text-sm text-black/60">{admin.email}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleAssignAdmin(admin._id)}
                          size="sm"
                          className="gap-2"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Assign
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-3xl w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Users className="h-8 w-8 text-black/60" />
                    </div>
                    <p className="text-black/80 font-medium">No available administrators</p>
                    <p className="text-sm text-black/60 mt-1">
                      All administrators are already assigned to plants
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlantManagement;